import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const Subscriptions = () => {
  const [showForm, setShowForm] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [formData, setFormData] = useState({
    planId: '',
    name: '',
    price: '',
    period: 'Monthly',
    features: '',
    bg: '#ffffff',
    border: '#e5e7eb',
    isPopular: false,
    image: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await api.get('/subscriptions');
      setPlans(response.data.data || response.data || []);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch data", err);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!formData.planId || !formData.name || !formData.price || !formData.period) {
      alert("Plan ID, Name, Price, and Period are required.");
      return;
    }

    const payload = {
      ...formData,
      features: typeof formData.features === 'string' ? formData.features.split(',').map(f => f.trim()).filter(f => f) : formData.features
    };

    try {
      if (editMode) {
        await api.put(`/subscriptions/${selectedId}`, payload);
        alert("✅ Subscription Plan updated successfully!");
      } else {
        await api.post(`/subscriptions`, payload);
        alert("✅ New Subscription Plan added!");
      }
      fetchData();
      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error("Save failed", err);
      alert("❌ Error saving plan.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this subscription plan?")) return;
    try {
      await api.delete(`/subscriptions/${id}`);
      alert("✅ Plan deleted!");
      fetchData();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setSelectedId(null);
    setFormData({
      planId: '',
      name: '',
      price: '',
      period: 'Monthly',
      features: '',
      bg: '#ffffff',
      border: '#e5e7eb',
      isPopular: false,
      image: ''
    });
  };

  const handleEditClick = (plan) => {
    setSelectedId(plan._id);
    setEditMode(true);
    setFormData({
      planId: plan.planId,
      name: plan.name,
      price: plan.price,
      period: plan.period || 'Monthly',
      features: Array.isArray(plan.features) ? plan.features.join(', ') : plan.features || '',
      bg: plan.bg || '#ffffff',
      border: plan.border || '#e5e7eb',
      isPopular: plan.isPopular || false,
      image: plan.image || ''
    });
    setShowForm(true);
  };

  return (
    <div className="p-8 bg-[#FAF9F6] min-h-screen">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-serif font-bold text-[#2A2858]">Subscription Plans</h1>
          <p className="text-stone-500 mt-1 text-sm">Manage premium access and membership tiers</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(!showForm); }}
          className="bg-[#E8B84B] hover:bg-[#b5952f] text-black px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg transition-colors flex items-center gap-2"
        >
          <span className="material-symbols-outlined text-[18px]">
            {showForm ? 'close' : 'add'}
          </span>
          {showForm ? 'Cancel' : 'Add Plan'}
        </button>
      </div>

      {/* Add New Plan Form */}
      {showForm && (
        <div className="bg-[#0E0E26] p-8 rounded-2xl mb-8 border border-[#E8B84B]/30 shadow-2xl transition-all">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white">Create New Subscription Plan</h2>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="text-stone-400 hover:text-white text-sm font-bold">Cancel</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Plan ID (Unique)</label>
              <input type="text" name="planId" value={formData.planId} onChange={handleInputChange} placeholder="e.g. vip_darshan" className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] placeholder-stone-600" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Plan Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="e.g. VIP Darshan" className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] placeholder-stone-600" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Price (₹)</label>
              <input type="number" name="price" value={formData.price} onChange={handleInputChange} placeholder="e.g. 999" className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] placeholder-stone-600" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Period</label>
              <select name="period" value={formData.period} onChange={handleInputChange} className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] appearance-none cursor-pointer">
                <option value="Monthly">Monthly</option>
                <option value="Yearly">Yearly</option>
                <option value="Lifetime">Lifetime</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Features (Comma Separated)</label>
              <input type="text" name="features" value={formData.features} onChange={handleInputChange} placeholder="Priority access, Daily SevaOffering, Special Aarti..." className="w-full bg-[#14122B] border border-stone-700 text-white px-4 py-3 rounded-lg focus:outline-none focus:border-[#E8B84B] placeholder-stone-600" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-2">Photo Image (Upload)</label>
              <div className="flex gap-4 items-center">
                <div className="w-12 h-12 bg-stone-800 rounded overflow-hidden flex items-center justify-center">
                  {formData.image ? <img src={formData.image} alt="preview" className="w-full h-full object-cover" /> : <span className="material-symbols-outlined text-stone-500">image</span>}
                </div>
                <input type="file" onChange={handleImageUpload} className="flex-1 bg-[#14122B] border border-stone-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:border-[#E8B84B] text-sm" />
              </div>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" id="isPopular" name="isPopular" checked={formData.isPopular} onChange={handleInputChange} className="w-4 h-4" />
              <label htmlFor="isPopular" className="text-[10px] font-bold text-stone-400 uppercase tracking-widest cursor-pointer">Mark as Popular</label>
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button onClick={handleSave} className="bg-[#E8B84B] hover:bg-[#b5952f] text-black px-8 py-2.5 rounded-lg font-bold transition-colors">
              Save Plan
            </button>
          </div>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-2xl border border-stone-100 shadow-sm overflow-hidden">
        {loading ? (
           <div className="p-10 text-center text-stone-500 font-bold animate-pulse">Loading Plans...</div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-stone-50 border-b border-stone-100 text-[10px] uppercase tracking-widest text-[#2A2858] font-bold">
                <th className="p-4 w-16">Image</th>
                <th className="p-4">Plan Name</th>
                <th className="p-4">Price</th>
                <th className="p-4">Period</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 ? (
                <tr>
                  <td colSpan="6" className="p-10 text-center text-stone-500 font-medium">
                    No active plans found
                  </td>
                </tr>
              ) : (
                plans.map(plan => (
                  <tr key={plan._id} className="border-b border-stone-50 hover:bg-stone-50">
                    <td className="p-4">
                      <div className="w-10 h-10 rounded bg-stone-100 flex justify-center items-center overflow-hidden">
                        {plan.image ? <img src={plan.image} alt={plan.name} className="w-full h-full object-cover" /> : <span className="material-symbols-outlined text-stone-300">workspace_premium</span>}
                      </div>
                    </td>
                    <td className="p-4 font-bold text-stone-800">
                      {plan.name} <span className="text-[10px] text-stone-400 font-mono ml-2">({plan.planId})</span>
                    </td>
                    <td className="p-4 text-[#2A2858] font-bold">₹{plan.price}</td>
                    <td className="p-4 text-stone-600 text-sm">{plan.period}</td>
                    <td className="p-4">
                      {plan.isPopular && <span className="bg-yellow-100 text-yellow-700 text-[10px] px-2 py-1 rounded-full font-bold">Popular</span>}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-center gap-3">
                        <button onClick={() => handleEditClick(plan)} className="text-stone-400 hover:text-[#2A2858] transition-colors" title="Edit Plan">
                          <span className="material-symbols-outlined text-xl">edit</span>
                        </button>
                        <button onClick={() => handleDelete(plan._id)} className="text-stone-400 hover:text-red-500 transition-colors" title="Delete Plan">
                          <span className="material-symbols-outlined text-xl">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default Subscriptions;